// "Ask AI": global slide-over + full-screen tab with chat history.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import posthog from "@/lib/posthog";
import { Icon } from "@/components/Icon";
import { Search } from "@/components/primitives";
import { useNotImplemented } from "@/hooks/use-not-implemented";
import { DATA } from "@/data";

interface Scenario {
  name: string; sub?: string; best?: boolean;
  tag?: { label: string; tone: "pos" | "warn" };
  stats: [string, string][];
}

interface ChatMsg {
  role: "user" | "ai";
  text: string;
  records?: { l: string; v: string }[];
  scenarios?: Scenario[];
  verdict?: string;
  cta?: { label: string; to: string };
}

/* Canned what-if simulations — same numbers as the scenario builder model. */
const WHATIF_MARKETING: ChatMsg = {
  role: "ai",
  text: "I simulated three ways to invest in revenue growth on your live financials — 12-month horizon, 62% gross margin, every assumption editable:",
  scenarios: [
    { name: "In-house marketing hire", sub: "$95k salary + 28% load", stats: [["12-mo cost", "$127,600"], ["Revenue lift", "$231,000"], ["ROI", "12.2%"], ["Break-even", "Mo 8"], ["Runway", "20.7 mo"]] },
    { name: "External marketing agency", best: true, tag: { label: "Recommended", tone: "pos" }, sub: "$8.5k/mo retainer", stats: [["12-mo cost", "$107,000"], ["Revenue lift", "$207,000"], ["ROI", "19.9%"], ["Break-even", "Mo 4"], ["Runway", "21.8 mo"]] },
    { name: "Focused paid social project", sub: "6-month campaign", stats: [["12-mo cost", "$80,000"], ["Revenue lift", "$121,500"], ["ROI", "−5.8%"], ["Break-even", ">12 mo"], ["Runway", "17.4 mo"]] },
  ],
  verdict: "The external agency is the strongest path — best ROI, fastest break-even, and runway extends to 21.8 months. The in-house hire produces the largest lift but pays back slowest; paid social doesn't clear break-even inside 12 months.",
  records: [{ l: "Live financials", v: "QuickBooks + bank feed" }, { l: "Assumptions", v: "editable" }],
  cta: { label: "Open in scenario builder", to: "/finance/cash?view=whatif" },
};

const WHATIF_ENGINEERS: ChatMsg = {
  role: "ai",
  text: "I modeled adding 3 senior engineers from July against your current forecast. Fully ramped they add $36,300/mo to burn ($145k average salary + 28% payroll load). Cash stays positive across the horizon, but the buffer thins:",
  scenarios: [
    { name: "Base plan", sub: "no new hires", stats: [["Burn", "$59.3k/mo"], ["Runway", "14.2 mo"], ["Break-even", "Q3 2027"]] },
    { name: "+3 engineers in Q3", tag: { label: "Below 12-mo floor", tone: "warn" }, sub: "all start July 1", stats: [["Burn", "$95.6k/mo"], ["Runway", "11.4 mo"], ["Break-even", "Q1 2028"]] },
  ],
  verdict: "Feasible but tight — runway lands below your 12-month floor. Staggering the hires one per month holds runway at 12.1 months, and pairing them with the agency path's revenue lift keeps it above 13. I can model either.",
  records: [{ l: "Cash & Forecast", v: "model" }, { l: "Payroll actuals", v: "QuickBooks" }],
  cta: { label: "Open in scenario builder", to: "/finance/cash?view=whatif" },
};

/* Canned informative answers — numbers match the KPI, cash and budget demo data. */
const BURN_MONTH: ChatMsg = {
  role: "ai",
  text: "Burn rate rose 18% to $59,300/mo this month. I traced the jump to three drivers:",
  scenarios: [
    { name: "LinkedIn Ads — new Q3 campaign", sub: "recurring", stats: [["Added", "+$5,400/mo"], ["Started", "Jun 4"], ["vs plan", "+18%"]] },
    { name: "HubSpot annual renewal", tag: { label: "One-off", tone: "warn" }, sub: "tier upgrade", stats: [["Added", "+$2,000"], ["Recurs", "Jun 2027"]] },
    { name: "Two new hires", sub: "started mid-month", stats: [["This month", "+$1,650"], ["Full run-rate", "+$4,900/mo"]] },
  ],
  verdict: "Excluding the one-off renewal, underlying run-rate is ~$52k/mo and stable. Budget Sentinel has a drafted review of the LinkedIn spend in your approvals queue — approving it caps the campaign at plan.",
  records: [{ l: "KPIs", v: "Burn rate" }, { l: "Budget Sentinel", v: "1 drafted review" }],
  cta: { label: "Open Burn rate KPI", to: "/finance/kpis" },
};

const BURN_QOQ: ChatMsg = {
  role: "ai",
  text: "Cash burn this quarter is $171,300 ($57.1k/mo average) against $145,200 ($48.4k/mo) last quarter — up 18%. Where the increase came from:",
  scenarios: [
    { name: "One-off annual renewals", tag: { label: "Non-recurring", tone: "warn" }, sub: "HubSpot, insurance", stats: [["Impact", "+$14,800"], ["Recurs", "next year"]] },
    { name: "New headcount", sub: "2 hires in May", stats: [["Impact", "+$8,900"], ["Planned", "yes"]] },
    { name: "Everything else", stats: [["Impact", "+$2,400"], ["Growth", "+1.7%"]] },
  ],
  verdict: "Excluding one-offs, underlying burn grew 6.2% — in line with the hiring plan. Run-rate returns to ~$52k/mo next quarter.",
  records: [{ l: "KPIs", v: "Burn rate" }, { l: "Cash & Forecast", v: "model" }],
  cta: { label: "Open Cash & Forecast", to: "/finance/cash" },
};

const TOP_EXPENSES: ChatMsg = {
  role: "ai",
  text: "Your top 5 expense categories last month — $172,900 total, 4.1% above the month before:",
  scenarios: [
    { name: "Payroll", stats: [["Amount", "$72,600"], ["Share", "42%"], ["MoM", "+3.1%"]] },
    { name: "AWS & cloud infrastructure", stats: [["Amount", "$41,500"], ["Share", "24%"], ["MoM", "+8.4%"]] },
    { name: "Logistics", stats: [["Amount", "$31,100"], ["Share", "18%"], ["MoM", "−2.2%"]] },
    { name: "Software & tooling", sub: "HubSpot renewal", stats: [["Amount", "$14,200"], ["Share", "8%"], ["MoM", "+16%"]] },
    { name: "Marketing", tag: { label: "Over budget", tone: "warn" }, sub: "new LinkedIn campaign", stats: [["Amount", "$13,500"], ["Share", "8%"], ["MoM", "+61%"]] },
  ],
  verdict: "Marketing's jump is the new LinkedIn campaign and software's is the one-off HubSpot renewal — everything else tracks plan. Payroll stays the anchor at 42% of spend.",
  records: [{ l: "Transactions", v: "214 records" }],
  cta: { label: "View in Transactions", to: "/finance/transactions" },
};

const OVERDUE_INVOICES: ChatMsg = {
  role: "ai",
  text: "Three invoices are more than 30 days overdue — $24,310 at risk:",
  scenarios: [
    { name: "Globex Corp — INV-2041", tag: { label: "Highest risk", tone: "warn" }, stats: [["Amount", "$9,840"], ["Overdue", "34 days"], ["History", "late 3 of last 4"]] },
    { name: "Initech — INV-2029", stats: [["Amount", "$8,270"], ["Overdue", "41 days"], ["Reminder", "sent Jun 12"]] },
    { name: "Umbrella Retail — INV-2035", stats: [["Amount", "$6,200"], ["Overdue", "32 days"], ["Promised", "Jun 24"]] },
  ],
  verdict: "Globex responds to calls, not email — I'd start there. Reminders were auto-sent to the other two and Umbrella promised payment by Jun 24. Collecting all three lifts your collection rate back above the 95% target.",
  records: [{ l: "Collections", v: "$24,310 open" }, { l: "Collection rate", v: "91.3%" }],
  cta: { label: "Open overdue invoices", to: "/finance/transactions" },
};

const REVENUE_CLIENTS: ChatMsg = {
  role: "ai",
  text: "YTD revenue by client — the top 5 account for 71% of $1.39M:",
  scenarios: [
    { name: "Wayne Enterprises", tag: { label: "Top client", tone: "pos" }, stats: [["YTD", "$312,400"], ["Share", "22%"], ["YoY", "+18%"]] },
    { name: "Acme Corp", stats: [["YTD", "$248,100"], ["Share", "18%"], ["YoY", "+9%"]] },
    { name: "Globex Corp", sub: "pays late", stats: [["YTD", "$187,300"], ["Share", "13%"], ["YoY", "flat"]] },
    { name: "Stark Industries", stats: [["YTD", "$142,800"], ["Share", "10%"], ["YoY", "+24%"]] },
    { name: "Initech", stats: [["YTD", "$98,200"], ["Share", "7%"], ["YoY", "−6%"]] },
  ],
  verdict: "Concentration is moderate — no client above 25%. Stark is growing fastest (+24%); Globex is flat and pays late, so its share may keep slipping.",
  records: [{ l: "Invoices", v: "YTD" }, { l: "Revenue", v: "$1.39M YTD" }],
};

const CASH_90D: ChatMsg = {
  role: "ai",
  text: "Base-case projection from the forecast model — cash stays comfortably positive across the next 90 days:",
  scenarios: [
    { name: "Today", stats: [["Balance", "$842,310"], ["Runway", "14.2 mo"]] },
    { name: "+30 days", sub: "after June payroll dip", stats: [["Balance", "$887,000"], ["Net", "+$44,700"]] },
    { name: "+60 days", stats: [["Balance", "$931,000"], ["Net", "+$44,000"]] },
    { name: "+90 days", tag: { label: "91% confidence", tone: "pos" }, stats: [["Balance", "$978,000"], ["Net", "+$47,000"]] },
  ],
  verdict: "The mid-June dip is payroll ($48k), covered twice over by the Stripe payout and Acme collection that follow. The trajectory reaches $1.03M by week 16. Biggest swing factor: the three overdue invoices ($24,310).",
  records: [{ l: "Cash & Forecast", v: "model" }, { l: "Assumptions", v: "editable" }],
  cta: { label: "Open Cash & Forecast", to: "/finance/cash" },
};

const MONTH_VS_PLAN: ChatMsg = {
  role: "ai",
  text: "This month against plan — ahead on every top line:",
  scenarios: [
    { name: "Revenue", tag: { label: "Ahead of plan", tone: "pos" }, stats: [["Actual", "$284,512"], ["Plan", "$254,000"], ["Variance", "+12%"]] },
    { name: "Expenses", stats: [["Actual", "$172,940"], ["Plan", "$166,100"], ["Variance", "+4.1%"]] },
    { name: "Net margin", tag: { label: "Ahead of plan", tone: "pos" }, stats: [["Actual", "39%"], ["Plan", "35%"], ["Variance", "+4 pts"]] },
    { name: "Cash", stats: [["Actual", "$842,310"], ["Plan", "$814,000"], ["Variance", "+$28k"]] },
  ],
  verdict: "The only line materially over plan is marketing (+18%, the new ad campaigns) — Budget Sentinel's drafted review is in your approvals queue. Otherwise no action needed.",
  records: [{ l: "Budget vs. forecast", v: "June" }, { l: "KPIs", v: "9 tracked" }],
  cta: { label: "Open Performance", to: "/finance/overview" },
};

const matchReply = (q: string): ChatMsg | null => {
  const s = q.toLowerCase();
  if (/engineer/.test(s)) return WHATIF_ENGINEERS;
  if (/(market|agency|paid social|paid ads)/.test(s)) return WHATIF_MARKETING;
  if (/quarter/.test(s) && /burn/.test(s)) return BURN_QOQ;
  if (/burn/.test(s)) return BURN_MONTH;
  if (/overdue/.test(s)) return OVERDUE_INVOICES;
  if (/expense/.test(s)) return TOP_EXPENSES;
  if (/by client/.test(s)) return REVENUE_CLIENTS;
  if (/forecast|90 days|cash on hand/.test(s)) return CASH_90D;
  if (/summar|plan/.test(s)) return MONTH_VS_PLAN;
  return null;
};

const aiReply = (q = ""): ChatMsg => matchReply(q) || {
  role: "ai",
  text: "Here's what I found — pulling from your live ledger, forecast model and agent activity. Every figure below links to its source record.",
  records: [{ l: "Related records", v: "3 linked" }, { l: "Cash & Forecast", v: "model" }],
};

type DislikeReason = "incorrect" | "too_generic" | "not_actionable" | "too_long" | "other";

const DISLIKE_REASONS: { value: DislikeReason; label: string }[] = [
  { value: "incorrect", label: "Incorrect" },
  { value: "too_generic", label: "Too generic" },
  { value: "not_actionable", label: "Not actionable" },
  { value: "too_long", label: "Too long" },
  { value: "other", label: "Other" },
];

/* Thumbs up/down on an AI answer — mirrors the like/dislike + reason capture
   from the (unused) AIMode chat, wired into the chat surface users actually see. */
const MessageFeedback = ({ surface, question }: { surface: "overlay" | "screen"; question: string }) => {
  const [state, setState] = useState<"none" | "liked" | "disliked">("none");
  const [showReasons, setShowReasons] = useState(false);

  const like = () => {
    if (state !== "none") return;
    setState("liked");
    setShowReasons(false);
    posthog.capture("ai_response_liked", { surface, question });
  };
  const toggleDislike = () => {
    if (state === "liked") return;
    const opening = !showReasons;
    setShowReasons(opening);
    if (opening) posthog.capture("ai_dislike_panel_opened", { surface, question });
  };
  const dislike = (reason: DislikeReason) => {
    setState("disliked");
    setShowReasons(false);
    posthog.capture("ai_response_disliked", { surface, question, reason });
  };

  return (
    <div style={{ marginTop: 11 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button onClick={like} disabled={state !== "none"} title="Good response"
          style={{ background: "none", border: "none", padding: 4, lineHeight: 0, cursor: state === "none" ? "pointer" : "default", opacity: state === "disliked" ? 0.35 : 1 }}>
          <Icon name="thumbs-up" size={14} stroke={state === "liked" ? "var(--pos)" : "var(--ink-4)"} />
        </button>
        <button onClick={toggleDislike} disabled={state === "liked"} title="Bad response"
          style={{ background: "none", border: "none", padding: 4, lineHeight: 0, cursor: state !== "liked" ? "pointer" : "default", opacity: state === "liked" ? 0.35 : 1 }}>
          <Icon name="thumbs-down" size={14} stroke={state === "disliked" ? "var(--neg)" : "var(--ink-4)"} />
        </button>
      </div>
      {showReasons && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
          {DISLIKE_REASONS.map((r) => (
            <button key={r.value} onClick={() => dislike(r.value)} className="chip chip-out" style={{ cursor: "pointer", border: "none", font: "inherit" }}>
              {r.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* Scenario comparison, verdict and builder CTA rendered inside an AI bubble. */
const AiExtras = ({ m, onNavigate }: { m: ChatMsg; onNavigate?: () => void }) => {
  const navigate = useNavigate();
  if (!m.scenarios && !m.verdict && !m.cta) return null;
  return <>
    {m.scenarios && (
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
        {m.scenarios.map((s, i) => (
          <div key={i} className="card" style={{
            padding: "11px 14px",
            border: s.best ? "1px solid var(--brand-line)" : "1px solid var(--line)",
            background: s.best ? "var(--brand-tint)" : "var(--surface)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12.5, fontWeight: 600 }}>{s.name}</span>
              {s.tag && <span className={"badge " + (s.tag.tone === "pos" ? "b-pos" : "b-warn")}>{s.tag.label}</span>}
              {s.sub && <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--ink-4)" }}>{s.sub}</span>}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px" }}>
              {s.stats.map((st) => (
                <span key={st[0]} style={{ fontSize: 11.5, color: "var(--ink-4)", whiteSpace: "nowrap" }}>
                  {st[0]} <b className="num" style={{ color: "var(--ink)", fontSize: 12 }}>{st[1]}</b>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    )}
    {m.verdict && (
      <div style={{ fontSize: 12.5, color: "var(--ink-2)", lineHeight: 1.55, marginTop: 10 }}>
        <b>Bottom line:</b> {m.verdict}
      </div>
    )}
    {m.cta && (
      <button className="btn btn-sec btn-sm" style={{ marginTop: 10 }}
        onClick={() => { posthog.capture("askai_cta_clicked", { label: m.cta!.label, to: m.cta!.to }); onNavigate?.(); navigate(m.cta!.to); }}>
        <Icon name="brain" size={14} stroke="var(--ink-3)" /> {m.cta.label}
      </button>
    )}
  </>;
};

/* ═══ Global slide-over ═════════════════════════════════════════════ */
export const AskAIOverlay = ({ open, onClose, seed, onExpand }: {
  open: boolean; onClose: () => void; seed?: string | null; onExpand?: () => void;
}) => {
  const [q, setQ] = useState("");
  const [thread, setThread] = useState<ChatMsg[]>([]);
  const seeded = useRef<string | null>(null);

  const send = (text?: string) => {
    const t = text || q;
    if (!t.trim()) return;
    posthog.capture("askai_question_submitted", { surface: "overlay", question: t, matched: !!matchReply(t) });
    setThread((p) => [...p, { role: "user", text: t }, aiReply(t)]);
    setQ("");
  };

  useEffect(() => {
    if (open && seed && seeded.current !== seed) {
      seeded.current = seed;
      setThread((p) => [...p, { role: "user", text: seed }, aiReply(seed)]);
    }
    if (!open) seeded.current = null;
  }, [open, seed]);

  if (!open) return null;
  return <>
    <div onClick={onClose} className="fade-up" style={{ position: "fixed", inset: 0, background: "rgba(23, 49, 58,.34)", zIndex: 70 }} />
    <div className="fade-up" style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 480, maxWidth: "92vw", background: "var(--surface)", zIndex: 71, boxShadow: "var(--sh-lg)", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "15px 20px", borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", gap: 11 }}>
        <span className="ai-mark" style={{ width: 28, height: 28, borderRadius: 8 }}><Icon name="spark" size={16} stroke="#fff" /></span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14.5, fontWeight: 600 }}>Ask AI</div>
          <div style={{ fontSize: 11.5, color: "var(--ink-4)" }}>Your AI finance team · {DATA.company.name}</div>
        </div>
        <button className="btn btn-ghost btn-icon" title="Open full screen" onClick={() => { posthog.capture("askai_expanded"); onClose(); onExpand?.(); }}>
          <Icon name="ext" size={16} stroke="var(--ink-3)" />
        </button>
        <button className="btn btn-ghost btn-icon" onClick={() => { posthog.capture("askai_overlay_closed", { messages: thread.length }); onClose(); }}><Icon name="x" size={17} stroke="var(--ink-3)" /></button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
        {thread.length === 0 ? (
          <div style={{ paddingTop: 8 }}>
            <div style={{ fontSize: 14, color: "var(--ink-2)", lineHeight: 1.6, marginBottom: 18 }}>
              Ask anything about Northwind's finances — I read your live ledger, forecasts and every agent's activity.
            </div>
            <div className="eyebrow" style={{ marginBottom: 10 }}>Suggested</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {DATA.aiSuggested.map((s, i) => (
                <button key={i} onClick={() => { posthog.capture("askai_suggestion_clicked", { surface: "overlay", suggestion: s }); send(s); }} className="card" style={{ padding: "12px 14px", textAlign: "left", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, font: "inherit", fontSize: 13.5 }}>
                  <Icon name="spark" size={15} stroke="var(--brand)" /> {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {thread.map((m, i) => m.role === "user" ? (
              <div key={i} style={{ alignSelf: "flex-end", maxWidth: "85%", background: "var(--brand)", color: "#fff", padding: "10px 14px", borderRadius: "14px 14px 4px 14px", fontSize: 13.5, lineHeight: 1.5 }}>{m.text}</div>
            ) : (
              <div key={i} style={{ display: "flex", gap: 10 }}>
                <span className="ai-mark" style={{ width: 26, height: 26, borderRadius: 8, flexShrink: 0 }}><Icon name="spark" size={14} stroke="#fff" /></span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5, lineHeight: 1.6, color: "var(--ink-2)" }}>{m.text}</div>
                  <AiExtras m={m} onNavigate={onClose} />
                  {m.records && (
                    <div style={{ display: "flex", gap: 8, marginTop: 11, flexWrap: "wrap" }}>
                      {m.records.map((r, j) => (
                        <div key={j} className="chip chip-out" style={{ height: 28 }}>
                          <Icon name="link" size={12} stroke="var(--brand)" /><span>{r.l}</span><span className="num" style={{ fontWeight: 600 }}>{r.v}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <MessageFeedback surface="overlay" question={thread[i - 1]?.text ?? ""} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ padding: 14, borderTop: "1px solid var(--line)" }}>
        <div style={{ display: "flex", gap: 9, alignItems: "flex-end", background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 12, padding: "8px 8px 8px 14px" }}>
          <textarea value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1}
            placeholder="Ask anything…" style={{ flex: 1, border: "none", outline: "none", resize: "none", font: "inherit", fontSize: 13.5, lineHeight: 1.5, background: "transparent", padding: "5px 0", maxHeight: 110 }} />
          <button className="btn btn-pri btn-icon" onClick={() => send()}><Icon name="send" size={15} stroke="#fff" /></button>
        </div>
        <div style={{ fontSize: 10.5, color: "var(--ink-4)", textAlign: "center", marginTop: 7 }}>
          Answers link to source records · agents may act with your approval
        </div>
      </div>
    </div>
  </>;
};

/* ═══ FULL-SCREEN ASK AI (AI Agents → Ask AI tab) ═══════════════════ */
export const AskAIScreen = () => {
  const notify = useNotImplemented();
  const [q, setQ] = useState("");
  const [model, setModel] = useState("anthropic");
  const [activeChat, setActiveChat] = useState<string | null>(null); // null = empty state
  const [thread, setThread] = useState<ChatMsg[]>([]);

  const send = (text?: string) => {
    const t = text || q;
    if (!t.trim()) return;
    posthog.capture("askai_question_submitted", { surface: "screen", question: t, matched: !!matchReply(t), model });
    setActiveChat(activeChat || "new");
    setThread((p) => [...p, { role: "user", text: t }, aiReply(t)]);
    setQ("");
  };
  const newChat = () => { posthog.capture("askai_new_chat_clicked"); setActiveChat(null); setThread([]); setQ(""); };
  const openChat = (c: { id: string; title: string; preview: string }) => {
    posthog.capture("askai_history_chat_opened", { title: c.title });
    setActiveChat(c.id);
    setThread([
      { role: "user", text: c.title },
      matchReply(c.title) ||
      { role: "ai", text: "Restored from your saved conversation. " + c.preview + ". Every figure links to its source record.", records: [{ l: "Related records", v: "3 linked" }] },
    ]);
  };

  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", background: "var(--surface)" }} className="fade-up">
      {/* History rail */}
      <div style={{ width: 270, borderRight: "1px solid var(--line)", display: "flex", flexDirection: "column", background: "var(--surface-2)", flexShrink: 0 }}>
        <div style={{ padding: "14px 14px 10px" }}>
          <button className="btn btn-pri" style={{ width: "100%" }} onClick={newChat}><Icon name="plus" size={15} stroke="#fff" /> New chat</button>
        </div>
        <div style={{ padding: "0 10px 8px" }}><Search placeholder="Search chats" w="100%" /></div>
        <div style={{ flex: 1, overflowY: "auto", padding: "4px 8px 12px" }}>
          {DATA.aiHistory.map((g) => (
            <div key={g.group} style={{ marginBottom: 10 }}>
              <div className="eyebrow" style={{ padding: "8px 8px 6px", fontSize: 10 }}>{g.group}</div>
              {g.items.map((c) => {
                const on = c.id === activeChat;
                return (
                  <button key={c.id} onClick={() => openChat(c)} style={{
                    width: "100%", textAlign: "left", border: "none", cursor: "pointer",
                    background: on ? "var(--brand-soft)" : "transparent", borderRadius: 9, padding: "9px 10px",
                    marginBottom: 2, display: "block", font: "inherit", transition: "background .1s",
                  }}
                    onMouseEnter={(e) => { if (!on) e.currentTarget.style.background = "var(--surface-3)"; }}
                    onMouseLeave={(e) => { if (!on) e.currentTarget.style.background = "transparent"; }}>
                    <div style={{ fontSize: 12.5, fontWeight: on ? 600 : 500, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.title}</div>
                    <div style={{ fontSize: 11, color: "var(--ink-4)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 1 }}>{c.preview}</div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        {/* Top bar: model selector */}
        <div style={{ height: 52, flexShrink: 0, borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <span className="ai-mark" style={{ width: 24, height: 24, borderRadius: 7 }}><Icon name="spark" size={13} stroke="#fff" /></span>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Ask AI</span>
            <span style={{ fontSize: 12, color: "var(--ink-4)" }}>· reads your live financials</span>
          </div>
          <div style={{ display: "inline-flex", background: "var(--surface-3)", borderRadius: 9, padding: 3, gap: 2 }}>
            {DATA.aiModels.map((m) => {
              const on = m.v === model;
              return (
                <button key={m.v} onClick={() => { posthog.capture("askai_model_changed", { model: m.v }); setModel(m.v); }} style={{
                  border: "none", cursor: "pointer", font: "inherit", fontSize: 12.5, fontWeight: on ? 600 : 500,
                  padding: "5px 12px", borderRadius: 6, background: on ? "#fff" : "transparent",
                  color: on ? "var(--ink)" : "var(--ink-3)", boxShadow: on ? "var(--sh-sm)" : "none",
                  display: "inline-flex", alignItems: "center", gap: 6, transition: "all .12s",
                }}>
                  <span style={{ width: 7, height: 7, borderRadius: 50, background: m.c }} />{m.l}
                </button>
              );
            })}
          </div>
        </div>

        {/* Conversation or empty state */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {!activeChat || thread.length === 0 ? (
            <div style={{ maxWidth: 720, margin: "0 auto", padding: "64px 24px 24px", textAlign: "center" }}>
              <span className="ai-mark" style={{ width: 56, height: 56, borderRadius: 16, margin: "0 auto 22px" }}><Icon name="spark" size={28} stroke="#fff" /></span>
              <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-.025em" }}>Ask anything about your finances</div>
              <div style={{ fontSize: 15, color: "var(--ink-3)", marginTop: 8 }}>Plain English. Real numbers. Backed by your live ledger and forecasts.</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 30, textAlign: "left" }}>
                {DATA.aiScreenSuggested.map((s, i) => (
                  <button key={i} onClick={() => { posthog.capture("askai_suggestion_clicked", { surface: "screen", suggestion: s }); send(s); }} className="card" style={{ padding: "15px 17px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, font: "inherit", fontSize: 13.5, fontWeight: 500, color: "var(--ink-2)", transition: "box-shadow .15s,border-color .15s" }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "var(--sh-md)"; e.currentTarget.style.borderColor = "var(--brand-line)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "var(--line)"; }}>
                    <span style={{ flex: 1 }}>{s}</span><Icon name="chevright" size={15} stroke="var(--ink-4)" />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ maxWidth: 760, margin: "0 auto", padding: "24px 24px 8px", display: "flex", flexDirection: "column", gap: 20 }}>
              {thread.map((m, i) => m.role === "user" ? (
                <div key={i} style={{ alignSelf: "flex-end", maxWidth: "80%", background: "var(--brand)", color: "#fff", padding: "11px 15px", borderRadius: "14px 14px 4px 14px", fontSize: 14, lineHeight: 1.5 }}>{m.text}</div>
              ) : (
                <div key={i} style={{ display: "flex", gap: 11 }}>
                  <span className="ai-mark" style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0 }}><Icon name="spark" size={15} stroke="#fff" /></span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, lineHeight: 1.6, color: "var(--ink-2)" }}>{m.text}</div>
                    <AiExtras m={m} />
                    {m.records && (
                      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                        {m.records.map((r, j) => (
                          <div key={j} className="chip chip-out" style={{ height: 30 }}>
                            <Icon name="link" size={13} stroke="var(--brand)" /><span>{r.l}</span><span className="num" style={{ fontWeight: 600 }}>{r.v}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 14, marginTop: 12, alignItems: "center" }}>
                      <span onClick={() => { posthog.capture("askai_save_to_memory_clicked"); notify("Save to Memory"); }} style={{ fontSize: 12, color: "var(--ink-4)", display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                        <Icon name="bookmark" size={13} stroke="var(--ink-4)" /> Save to Memory
                      </span>
                      <span onClick={() => { posthog.capture("askai_turn_into_report_clicked"); notify("Turn into report"); }} style={{ fontSize: 12, color: "var(--ink-4)", display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                        <Icon name="doc" size={13} stroke="var(--ink-4)" /> Turn into report
                      </span>
                    </div>
                    <MessageFeedback surface="screen" question={thread[i - 1]?.text ?? ""} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Composer */}
        <div style={{ flexShrink: 0, padding: "12px 24px 20px", borderTop: "1px solid var(--line-2)" }}>
          <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", gap: 10, alignItems: "flex-end", background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 14, padding: "10px 10px 10px 16px", boxShadow: "var(--sh-md)" }}>
            <span className="ai-mark" style={{ width: 24, height: 24, marginBottom: 5 }}><Icon name="spark" size={13} stroke="#fff" /></span>
            <textarea value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1}
              placeholder="Ask anything about Northwind's finances…"
              style={{ flex: 1, border: "none", outline: "none", resize: "none", font: "inherit", fontSize: 14, lineHeight: 1.5, background: "transparent", padding: "6px 0", maxHeight: 120 }} />
            <button className="btn btn-pri btn-icon" onClick={() => send()}><Icon name="send" size={16} stroke="#fff" /></button>
          </div>
          <div style={{ fontSize: 11, color: "var(--ink-4)", textAlign: "center", marginTop: 8 }}>
            Answers link to source records · agents may act with your approval
          </div>
        </div>
      </div>
    </div>
  );
};
